import { AlertCircle, ArrowRight, CheckCircle2, Download, Loader2, Upload, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import type { BulkTransferLocationsResultDto } from '../../types/api';
import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';

interface BulkTransferLocationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownloadTemplate: () => void;
  onPreview: (file: File) => Promise<BulkTransferLocationsResultDto>;
  onCommit: (file: File) => Promise<BulkTransferLocationsResultDto>;
  isDownloading?: boolean;
  isPreviewing?: boolean;
  isCommitting?: boolean;
}

type Step = 'upload' | 'preview' | 'committed';

function ResultTable({ result }: { result: BulkTransferLocationsResultDto }) {
  const okRows = result.rows.filter(r => r.status === 'ok');
  const errorRows = result.rows.filter(r => r.status === 'error');

  return (
    <div className='space-y-4'>
      <div className='flex gap-3'>
        <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-700'>
          <CheckCircle2 className='h-4 w-4' />
          {okRows.length} {result.dryRun ? 'ready' : 'transferred'}
        </span>
        {errorRows.length > 0 && (
          <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-700'>
            <AlertCircle className='h-4 w-4' />
            {errorRows.length} error{errorRows.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {okRows.length > 0 && (
        <div>
          <p className='text-sm font-medium text-gray-700 mb-2'>
            {result.dryRun ? 'Will be transferred' : 'Transferred'}
          </p>
          <div className='rounded-lg border border-green-100 overflow-x-auto'>
            <table className='min-w-full text-sm'>
              <thead className='bg-green-50'>
                <tr>
                  <th className='px-3 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider'>Row</th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider'>Email</th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider'>Current location</th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider'></th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider'>New location</th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-green-50'>
                {okRows.map(r => (
                  <tr key={r.row}>
                    <td className='px-3 py-2 text-gray-500'>{r.row}</td>
                    <td className='px-3 py-2 text-gray-800'>{r.email}</td>
                    <td className='px-3 py-2 text-gray-500'>{r.currentLocations || '(none)'}</td>
                    <td className='px-3 py-2 text-gray-400'>
                      <ArrowRight className='h-3.5 w-3.5' />
                    </td>
                    <td className='px-3 py-2 text-gray-800 font-medium'>{r.newLocation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {errorRows.length > 0 && (
        <div>
          <p className='text-sm font-medium text-gray-700 mb-2'>Errors — fix and re-upload</p>
          <div className='rounded-lg border border-red-100 overflow-x-auto'>
            <table className='min-w-full text-sm'>
              <thead className='bg-red-50'>
                <tr>
                  <th className='px-3 py-2 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>Row</th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>Email</th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>Reason</th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-red-50'>
                {errorRows.map(r => (
                  <tr key={r.row}>
                    <td className='px-3 py-2 text-gray-500'>{r.row}</td>
                    <td className='px-3 py-2 text-gray-800'>{r.email || '-'}</td>
                    <td className='px-3 py-2 text-red-600'>{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export const BulkTransferLocationsDialog: React.FC<BulkTransferLocationsDialogProps> = ({
  open,
  onOpenChange,
  onDownloadTemplate,
  onPreview,
  onCommit,
  isDownloading = false,
  isPreviewing = false,
  isCommitting = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [step, setStep] = useState<Step>('upload');
  const [previewResult, setPreviewResult] = useState<BulkTransferLocationsResultDto | null>(null);
  const [finalResult, setFinalResult] = useState<BulkTransferLocationsResultDto | null>(null);
  const [fileError, setFileError] = useState('');

  const busy = isPreviewing || isCommitting;

  const handleClose = () => {
    if (busy) return;
    setSelectedFile(null);
    setStep('upload');
    setPreviewResult(null);
    setFinalResult(null);
    setFileError('');
    onOpenChange(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFileError('');
    if (file && !file.name.endsWith('.csv')) {
      setFileError('Only .csv files are accepted');
      setSelectedFile(null);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handlePreview = async () => {
    if (!selectedFile) return;
    const data = await onPreview(selectedFile);
    setPreviewResult(data);
    setStep('preview');
  };

  const handleCommit = async () => {
    if (!selectedFile) return;
    const data = await onCommit(selectedFile);
    setFinalResult(data);
    setStep('committed');
  };

  const handleReset = () => {
    setSelectedFile(null);
    setStep('upload');
    setPreviewResult(null);
    setFinalResult(null);
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent showCloseButton={!busy} className='max-w-3xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-start gap-3'>
            <Upload className='h-5 w-5 text-primary mt-0.5 flex-shrink-0' />
            <div>
              <DialogTitle>Bulk Transfer Locations</DialogTitle>
              <DialogDescription className='mt-1'>
                Download the template, fill in a target location per user, preview the changes, then commit.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === 'upload' && (
          <div className='space-y-5 pt-1'>
            <div className='rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4'>
              <p className='text-sm font-medium text-gray-700 mb-1'>Step 1 — Download the template</p>
              <p className='text-xs text-gray-500 mb-3'>
                Columns: <code className='bg-gray-100 px-1 rounded'>email</code>,{' '}
                <code className='bg-gray-100 px-1 rounded'>province</code>,{' '}
                <code className='bg-gray-100 px-1 rounded'>district</code>,{' '}
                <code className='bg-gray-100 px-1 rounded'>sector</code>,{' '}
                <code className='bg-gray-100 px-1 rounded'>cell</code>,{' '}
                <code className='bg-gray-100 px-1 rounded'>village</code>. Leave deeper columns blank
                to assign at a higher level — but don't skip a level in the middle.
              </p>
              <Button type='button' variant='outline' size='sm' onClick={onDownloadTemplate} disabled={isDownloading}>
                {isDownloading ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : (
                  <Download className='h-4 w-4 mr-2' />
                )}
                Download template
              </Button>
            </div>

            <div>
              <p className='text-sm font-medium text-gray-700 mb-2'>Step 2 — Upload your filled CSV</p>
              <label
                htmlFor='bulk-transfer-file'
                className={`flex flex-col items-center justify-center w-full h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                  selectedFile
                    ? 'border-primary bg-secondary/30'
                    : 'border-gray-300 bg-gray-50 hover:border-primary hover:bg-secondary/10'
                } ${fileError ? 'border-red-400' : ''}`}
              >
                {selectedFile ? (
                  <div className='flex flex-col items-center gap-1'>
                    <CheckCircle2 className='h-6 w-6 text-primary' />
                    <span className='text-sm font-medium text-gray-700'>{selectedFile.name}</span>
                    <span className='text-xs text-gray-400'>{(selectedFile.size / 1024).toFixed(1)} KB</span>
                  </div>
                ) : (
                  <div className='flex flex-col items-center gap-1'>
                    <Upload className='h-6 w-6 text-gray-400' />
                    <span className='text-sm text-gray-500'>Click to select a .csv file</span>
                    <span className='text-xs text-gray-400'>Max 2 MB</span>
                  </div>
                )}
                <input
                  id='bulk-transfer-file'
                  ref={fileInputRef}
                  type='file'
                  accept='.csv'
                  className='hidden'
                  onChange={handleFileChange}
                  disabled={busy}
                />
              </label>
              {fileError && (
                <p className='mt-1 text-xs text-red-600 flex items-center gap-1'>
                  <AlertCircle className='h-3 w-3' />
                  {fileError}
                </p>
              )}
              {selectedFile && (
                <button
                  type='button'
                  onClick={() => {
                    setSelectedFile(null);
                    setFileError('');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className='mt-1 text-xs text-gray-400 hover:text-red-500 flex items-center gap-1'
                  disabled={busy}
                >
                  <X className='h-3 w-3' />
                  Remove file
                </button>
              )}
            </div>
          </div>
        )}

        {step === 'preview' && previewResult && (
          <div className='space-y-4 pt-1'>
            <p className='text-sm text-gray-600'>
              Dry run only — nothing has been changed yet. Review the rows below, then commit when ready.
            </p>
            <ResultTable result={previewResult} />
          </div>
        )}

        {step === 'committed' && finalResult && (
          <div className='space-y-4 pt-1'>
            <ResultTable result={finalResult} />
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && (
            <>
              <Button type='button' variant='outline' onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
              <Button type='button' onClick={handlePreview} disabled={!selectedFile || busy}>
                {isPreviewing ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin mr-2' />
                    Checking…
                  </>
                ) : (
                  'Preview (dry run)'
                )}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button type='button' variant='outline' onClick={handleReset} disabled={busy}>
                Upload another file
              </Button>
              <Button type='button' onClick={handleCommit} disabled={busy || previewResult.summary.ok === 0}>
                {isCommitting ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin mr-2' />
                    Transferring…
                  </>
                ) : (
                  `Commit ${previewResult.summary.ok} transfer${previewResult.summary.ok === 1 ? '' : 's'}`
                )}
              </Button>
            </>
          )}
          {step === 'committed' && (
            <>
              {finalResult && finalResult.summary.error > 0 && (
                <Button type='button' variant='outline' onClick={handleReset}>
                  Upload another file
                </Button>
              )}
              <Button type='button' onClick={handleClose}>
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkTransferLocationsDialog;
